<script lang="ts">
    import Fa from 'svelte-fa'
    import { faPlus } from '@fortawesome/free-solid-svg-icons'
    import FieldHeading from './field-heading.svelte'
    import DynamicTableSignatureCell from './dynamic-table-signature-cell.svelte'
    import type { DynamicTableColumn, DynamicTableData, DynamicTableRow, DynamicTableSignatureMeta, DynamicTableValue } from '../../types/dynamic-table'
    import { computeRowNumbers, isRowActive, isRowLocked, isSignatureMeta } from '../../types/dynamic-table'
    import { currentOffsetMinutes } from '../../utils/format-datetime'

    export let label: string
    export let data: DynamicTableData
    export let value: DynamicTableValue | null = null
    export let required = false
    export let readOnly = false
    // Passados pelo form-fill via svelte:component (Svelte ignora nos outros blocos).
    export let formId: string | null = null
    export let blockId: string | null = null
    // Callback opcional: notifica o form-fill que updatedAt mudou por fora do "Salvar" geral.
    export let onUpdatedAtChanged: ((updatedAt: string) => void) | null = null

    let addingRow = false
    let addRowError = ''

    $: rows = value?.rows ?? []
    $: rowNumbers = computeRowNumbers(rows)
    $: hasPersistedForm = !!formId
    $: canAddRow = !readOnly
    $: canManageActive = !readOnly

    function extractTableValue(content: Record<string, unknown>): DynamicTableValue {
        const answers = content.answers as Record<string, unknown> | undefined
        const extracted = blockId ? (answers?.[blockId] as DynamicTableValue | undefined) : undefined
        return extracted ?? { rows: [] }
    }

    // addRow/onSignatureRowUpdated chamam o backend direto (fora do "Salvar" geral do form-fill),
    // que resolve a linha a partir do documento já persistido — sem edições locais ainda não salvas
    // de OUTRAS linhas/células desta mesma tabela. Por isso nunca substituímos "value" inteiro pela
    // resposta do servidor: isso jogaria fora qualquer coisa que a pessoa tenha digitado e ainda não
    // salvo. Em vez disso, mesclamos só o que a chamada realmente mudou (a linha nova / a célula
    // assinada) por cima do estado local atual.
    async function addRow() {
        if (!blockId || !hasPersistedForm) return
        addingRow = true
        addRowError = ''
        try {
            // As colunas "Data/Hora (automática)" são preenchidas no servidor, que roda em UTC e não
            // conhece o fuso do consultório — por isso ele vai junto na requisição.
            const { content, updatedAt } = await window.api.tools.formsV2.forms.dynamicTableAddRow(formId!, blockId, currentOffsetMinutes())
            const serverValue = extractTableValue(content)
            const existingIds = new Set(rows.map((r) => r.id))
            const newRows = serverValue.rows.filter((r) => !existingIds.has(r.id))
            value = { rows: [...rows, ...newRows] }
            onUpdatedAtChanged?.(updatedAt)
        } catch {
            addRowError = 'Não foi possível adicionar a linha. Tente novamente.'
        } finally {
            addingRow = false
        }
    }

    function onSignatureRowUpdated(event: CustomEvent<{ content: Record<string, unknown>; updatedAt: string; rowId: string; columnId: string }>) {
        const { rowId, columnId } = event.detail
        const serverValue = extractTableValue(event.detail.content)
        const signedRow = serverValue.rows.find((r) => r.id === rowId)
        if (signedRow) {
            value = {
                rows: rows.map((r) => (r.id === rowId ? { ...r, cells: { ...r.cells, [columnId]: signedRow.cells[columnId] }, status: signedRow.status } : r)),
            }
        }
        onUpdatedAtChanged?.(event.detail.updatedAt)
    }

    function inputValue(e: Event): string {
        return (e.currentTarget as HTMLInputElement).value
    }

    function updateCell(rowId: string, columnId: string, newValue: string) {
        value = {
            rows: rows.map((row) => (row.id === rowId ? { ...row, cells: { ...row.cells, [columnId]: newValue } } : row)),
        }
    }

    function toggleActive(rowId: string) {
        value = {
            rows: rows.map((row) => (row.id === rowId ? { ...row, active: !isRowActive(row) } : row)),
        }
    }

    function formatDateCell(v: unknown): string {
        const str = String(v ?? '')
        const [y, m, d] = str.split('-')
        return d && m && y ? `${d}/${m}/${y}` : str
    }

    function asSignatureMeta(value: unknown): DynamicTableSignatureMeta {
        return value as DynamicTableSignatureMeta
    }

    function cellDisabled(row: DynamicTableRow): boolean {
        return readOnly || isRowLocked(row) || !isRowActive(row)
    }
</script>

<div class="view-field">
    <FieldHeading {label} {required} {readOnly} />

    {#if !data?.columns?.length}
        <p class="table-empty">Nenhuma coluna configurada.</p>
    {:else}
        <div class="table-scroll">
            <table class="dynamic-table">
                <thead>
                    <tr>
                        {#each data.columns as column (column.id)}
                            <th>{column.label}</th>
                        {/each}
                        {#if canManageActive}
                            <th class="actions-col">Ações</th>
                        {/if}
                    </tr>
                </thead>
                <tbody>
                    {#if rows.length === 0}
                        <tr>
                            <td class="table-empty-row" colspan={data.columns.length + (canManageActive ? 1 : 0)}>Nenhuma linha adicionada ainda.</td>
                        </tr>
                    {/if}
                    {#each rows as row, rowIndex (row.id)}
                        <tr class:locked={isRowLocked(row)} class:deactivated={!isRowActive(row)}>
                            {#each data.columns as column (column.id)}
                                <td>
                                    {#if column.type === 'row_number'}
                                        <span class="static-cell">{rowNumbers[rowIndex] ?? '—'}</span>
                                    {:else if column.type === 'signature'}
                                        {#if blockId && formId}
                                            <DynamicTableSignatureCell
                                                {formId}
                                                blockId={blockId}
                                                {row}
                                                {column}
                                                disabled={readOnly || !isRowActive(row)}
                                                on:rowUpdated={onSignatureRowUpdated}
                                            />
                                        {:else}
                                            <span class="signature-badge pending">Pendente</span>
                                        {/if}
                                    {:else if column.type === 'date' || column.type === 'time'}
                                        <span class="static-cell">
                                            {#if row.cells[column.id]}
                                                {column.type === 'date' ? formatDateCell(row.cells[column.id]) : row.cells[column.id]}
                                            {:else}
                                                —
                                            {/if}
                                        </span>
                                    {:else if cellDisabled(row)}
                                        <span class="static-cell">{row.cells[column.id] || '—'}</span>
                                    {:else if column.type === 'number'}
                                        <input
                                            type="number"
                                            class="cell-input"
                                            value={row.cells[column.id] ?? ''}
                                            on:input={(e) => updateCell(row.id, column.id, inputValue(e))}
                                        />
                                    {:else}
                                        <input
                                            type="text"
                                            class="cell-input"
                                            value={row.cells[column.id] ?? ''}
                                            on:input={(e) => updateCell(row.id, column.id, inputValue(e))}
                                        />
                                    {/if}
                                </td>
                            {/each}
                            {#if canManageActive}
                                <td class="actions-col">
                                    <button
                                        type="button"
                                        class="toggle-active-btn"
                                        class:reactivate={!isRowActive(row)}
                                        on:click={() => toggleActive(row.id)}
                                    >
                                        {isRowActive(row) ? 'Desativar' : 'Reativar'}
                                    </button>
                                </td>
                            {/if}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        {#if canAddRow}
            <button type="button" class="add-row-btn" on:click={addRow} disabled={addingRow || !hasPersistedForm}>
                <Fa icon={faPlus} size="xs" />
                <span>{addingRow ? 'Adicionando...' : 'Adicionar linha'}</span>
            </button>
            {#if !hasPersistedForm}
                <p class="table-hint">Salve o formulário antes de adicionar linhas.</p>
            {/if}
            {#if addRowError}
                <p class="table-error">{addRowError}</p>
            {/if}
        {/if}
    {/if}
</div>

<style>
    .view-field {
        display: flex;
        flex-direction: column;
        width: 100%;
    }
    .table-scroll {
        overflow-x: auto;
    }
    .dynamic-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85rem;
    }
    .dynamic-table th,
    .dynamic-table td {
        border: 1px solid #e9ecef;
        padding: 8px 10px;
        text-align: left;
        vertical-align: middle;
    }
    .dynamic-table th {
        background: #f8f9fa;
        color: #495057;
        font-weight: 600;
        font-size: 0.78rem;
        white-space: nowrap;
    }
    tr.locked {
        background: #fafafa;
    }
    tr.deactivated {
        background: #f8f9fa;
    }
    tr.deactivated .static-cell,
    tr.deactivated .cell-input {
        color: #adb5bd;
        opacity: 0.7;
    }
    .actions-col {
        white-space: nowrap;
    }
    .toggle-active-btn {
        border: 1px solid #dee2e6;
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 0.75rem;
        font-weight: 500;
        background: #fff;
        color: #495057;
        cursor: pointer;
    }
    .toggle-active-btn:hover:not(:disabled) {
        border-color: #fa5252;
        color: #fa5252;
    }
    .toggle-active-btn.reactivate:hover:not(:disabled) {
        border-color: var(--cs2-accent, #1bdf6f);
        color: var(--cs2-accent, #1bdf6f);
    }
    .toggle-active-btn:disabled {
        cursor: not-allowed;
    }
    .cell-input {
        width: 100%;
        min-width: 90px;
        border: 1px solid transparent;
        border-radius: 6px;
        padding: 4px 6px;
        font-size: 0.85rem;
        background: transparent;
        box-sizing: border-box;
    }
    .cell-input:hover {
        border-color: #dee2e6;
    }
    .cell-input:focus {
        outline: none;
        border-color: var(--cs2-accent, #1bdf6f);
        background: #fff;
    }
    .static-cell {
        color: #495057;
        white-space: nowrap;
    }
    .table-empty,
    .table-empty-row {
        color: #adb5bd;
        font-size: 0.85rem;
        text-align: center;
        padding: 10px;
    }
    .add-row-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: 1px dashed #ced4da;
        border-radius: 8px;
        padding: 7px 14px;
        margin-top: 10px;
        font-size: 0.82rem;
        color: #495057;
        background: #fff;
        cursor: pointer;
    }
    .add-row-btn:hover:not(:disabled) {
        border-color: var(--cs2-accent, #1bdf6f);
        color: var(--cs2-accent, #1bdf6f);
    }
    .add-row-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    .table-hint {
        margin: 4px 0 0;
        font-size: 0.75rem;
        color: #868e96;
    }
    .table-error {
        margin: 4px 0 0;
        font-size: 0.75rem;
        color: #c92a2a;
    }
    .signature-badge {
        display: inline-block;
        font-size: 0.78rem;
        color: #868e96;
    }
</style>
